import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Box, Typography, CircularProgress, Paper, Container } from '@mui/material';
import { userService, organizationService, teamService, subscriptionService, paymentService, invitationService, roleService, contentService, tagService, searchService, authService } from '../services';

const OAuthCallback: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUser } = useAuth();

  useEffect(() => {
    const handleOAuthCallback = async () => {
      // Parse query parameters from the URL
      const params = new URLSearchParams(location.search);
      const accessToken = params.get('accessToken');
      const refreshToken = params.get('refreshToken');
      const isNewUser = params.get('isNewUser') === 'true';
      const error = params.get('error');

      // Handle error case
      if (error) {
        setError(decodeURIComponent(error));
        return;
      }

      // If we have tokens, store them and update auth state
      if (accessToken && refreshToken) {
        try {
          // Store tokens in localStorage
          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('refreshToken', refreshToken);
          
          // Update the authentication state
          await refreshUser();
          
          // Redirect to the appropriate page
          if (isNewUser) {
            navigate('/profile', { state: { newUser: true } });
          } else {
            navigate('/dashboard');
          }
        } catch (err) {
          setError('Failed to authenticate with OAuth provider.');
          console.error('OAuth authentication error:', err);
        }
      } else {
        setError('No authentication tokens received from OAuth provider.');
      }
    };

    handleOAuthCallback();
  }, [location, navigate, refreshUser]);

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          mt: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper 
          sx={{ 
            p: 4, 
            width: '100%', 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {error ? (
            <>
              <Typography variant="h5" color="error" gutterBottom>
                Authentication Failed
              </Typography>
              <Typography color="text.secondary">{error}</Typography>
              <Box mt={3}>
                <Typography variant="body2">
                  <a href="/login" style={{ textDecoration: 'none' }}>
                    Return to login
                  </a>
                </Typography>
              </Box>
            </>
          ) : (
            <>
              <CircularProgress size={60} />
              <Typography variant="h6" sx={{ mt: 2 }}>
                Completing authentication...
              </Typography>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default OAuthCallback;